if (!customElements.get('wm-suite-demo')) {
  customElements.define('wm-suite-demo', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.buttons = [...this.querySelectorAll('[data-mode], [data-bypass]')];
      this.tabs = [...this.querySelectorAll('[data-chain-tab]')];
      this.panels = [...this.querySelectorAll('[data-chain-panel]')];
      this.playButton = this.querySelector('[data-play-button]');
      this.status = this.querySelector('[data-status]');
      this.seek = this.querySelector('[data-seek]');
      this.volume = this.querySelector('[data-volume]');
      this.time = this.querySelector('[data-time]');
      this.timeTotal = this.querySelector('[data-time-total]');
      this.counter = this.querySelector('[data-active-count]');
      this.disclosures = [...this.querySelectorAll('[data-disclosure]')];
      this.cache = new Map();
      this.mode = 'wet';
      this.offset = 0;
      this.playing = false;
      this.busy = false;
      this.serial = 0;
      const on = (el, name, fn) => el.addEventListener(name, fn, { signal: this.controller.signal });
      const ready = !!(this.dataset.dry && this.dataset.wet && window.AudioContext);
      this.playButton.disabled = !ready;
      this.buttons.forEach(button => { button.disabled = !ready || (button.hasAttribute('data-bypass') && !button.dataset.bypass); });
      this.status.textContent = ready ? this.dataset.wetLabel : this.dataset.missing;
      on(this.playButton, 'click', () => this.toggle());
      on(this.volume, 'input', () => {
        this.volume.style?.setProperty('--fill', `${Number(this.volume.value) * 100}%`);
        if (this.gain) this.gain.gain.setTargetAtTime(Number(this.volume.value), this.context.currentTime, 0.015);
      });
      on(this.seek, 'input', () => {
        this.offset = Number(this.seek.value);
        if (this.playing) this.start();
        this.paint();
      });
      this.buttons.forEach(button => on(button, 'click', () => this.select(button)));
      this.disclosures.forEach(button => on(button, 'click', () => this.expand(button)));
      this.tabs.forEach((tab, index) => {
        on(tab, 'click', () => this.selectTab(tab));
        on(tab, 'keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          this.selectTab(this.tabs[next]);
          this.tabs[next].focus();
        });
      });
      on(document, 'wm:suite-audio-play', event => { if (event.detail !== this) this.pause(); });
      document.addEventListener('play', event => { if (event.target instanceof HTMLMediaElement && !event.target.muted) this.pause(); }, { capture: true, signal: this.controller.signal });
      on(window, 'pagehide', () => this.pause());
      if (ready) this.showMode();
      this.paint();
    }
    expand(button) {
      const row = button.closest('[data-insert]');
      const open = button.getAttribute('aria-expanded') !== 'true';
      this.disclosures.forEach(other => {
        if (other === button) return;
        other.setAttribute('aria-expanded', 'false');
        const otherRow = other.closest('[data-insert]');
        if (otherRow) delete otherRow.dataset.open;
      });
      button.setAttribute('aria-expanded', String(open));
      if (!row) return;
      if (open) row.dataset.open = ''; else delete row.dataset.open;
      const image = open && row.querySelector('[data-dropdown] img[data-src]');
      if (image) { image.src = image.dataset.src; delete image.dataset.src; }
    }
    selectTab(tab) {
      this.tabs.forEach(item => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      this.panels.forEach(panel => { panel.hidden = panel.dataset.chainPanel !== tab.dataset.chainTab; });
      this.disclosures.forEach(button => {
        if (button.getAttribute('aria-expanded') !== 'true') return;
        if (button.closest('[data-chain-panel]')?.dataset.chainPanel !== tab.dataset.chainTab) this.expand(button);
      });
    }
    async getBuffer(url) {
      if (!this.cache.has(url)) {
        const task = fetch(url, { signal: AbortSignal.any([this.controller.signal, AbortSignal.timeout(25000)]) }).then(response => {
          if (!response.ok) throw new Error('Audio unavailable');
          return response.arrayBuffer();
        }).then(bytes => this.context.decodeAudioData(bytes));
        this.cache.set(url, task);
        task.catch(() => this.cache.delete(url));
        for (const key of this.cache.keys()) {
          if (this.cache.size <= 5) break;
          if (![this.dataset.dry, this.dataset.wet, this.mode, url].includes(key)) this.cache.delete(key);
        }
      }
      return this.cache.get(url);
    }
    async prepare() {
      if (!this.context) {
        this.context = new AudioContext();
        this.gain = this.context.createGain();
        this.gain.gain.value = Number(this.volume.value);
        this.gain.connect(this.context.destination);
      }
      await this.context.resume();
      if (!this.duration) {
        const [dry, wet] = await Promise.all([this.getBuffer(this.dataset.dry), this.getBuffer(this.dataset.wet)]);
        const duration = Math.min(dry.duration, wet.duration);
        const buffer = this.mode === 'dry' ? dry : this.mode === 'wet' ? wet : await this.getBuffer(this.mode);
        if (buffer.duration + 0.05 < duration) throw new Error('Bypass render too short');
        this.duration = duration;
        this.buffer = buffer;
        this.seek.max = this.duration;
        this.seek.disabled = false;
      }
    }
    current() {
      return this.playing ? Math.min(this.duration, this.offset + this.context.currentTime - this.startedAt) : this.offset;
    }
    stopSource() {
      if (!this.source) return;
      this.source.onended = null;
      this.source.stop();
      this.source.disconnect();
      this.source = null;
    }
    start() {
      this.stopSource();
      if (this.offset >= this.duration) this.offset = 0;
      this.source = this.context.createBufferSource();
      this.source.buffer = this.buffer;
      this.source.connect(this.gain);
      this.startedAt = this.context.currentTime;
      this.playing = true;
      const source = this.source;
      this.source.onended = () => {
        source.disconnect();
        this.playing = false;
        this.offset = 0;
        this.source = null;
        this.paint();
      };
      this.source.start(0, this.offset, this.duration - this.offset);
      this.paint();
      cancelAnimationFrame(this.frame);
      const tick = () => { this.paint(); if (this.playing) this.frame = requestAnimationFrame(tick); };
      this.frame = requestAnimationFrame(tick);
    }
    pause() {
      this.serial++;
      this.offset = this.current();
      this.playing = false;
      this.stopSource();
      cancelAnimationFrame(this.frame);
      this.paint();
    }
    async toggle() {
      if (this.busy) return;
      if (this.playing) { this.pause(); return; }
      this.busy = true;
      this.dataset.busy = '';
      this.playButton.disabled = true;
      this.status.textContent = this.dataset.loading;
      const token = ++this.serial;
      try {
        await this.prepare();
        if (!this.isConnected || token !== this.serial) return;
        document.dispatchEvent(new CustomEvent('wm:suite-audio-play', { detail: this }));
        document.querySelectorAll('video, audio').forEach(media => media.pause());
        this.start();
        this.showMode();
      } catch (error) {
        if (error.name !== 'AbortError') this.status.textContent = this.dataset.error;
      } finally {
        this.busy = false;
        delete this.dataset.busy;
        this.playButton.disabled = false;
        this.paint();
      }
    }
    async select(button) {
      if (this.busy) return;
      let mode = button.dataset.mode || (button.getAttribute('aria-pressed') === 'true' ? 'wet' : button.dataset.bypass);
      if (mode === 'dry' && this.mode === 'dry') mode = 'wet';
      if (!this.context) {
        this.mode = mode;
        this.showMode();
        return;
      }
      this.busy = true;
      this.dataset.busy = '';
      this.playButton.disabled = true;
      this.status.textContent = this.dataset.loading;
      const token = ++this.serial;
      try {
        const next = await this.getBuffer(mode === 'wet' ? this.dataset.wet : mode === 'dry' ? this.dataset.dry : mode);
        if (!this.isConnected || token !== this.serial) return;
        if (next.duration + 0.05 < this.duration) throw new Error('Bypass render too short');
        this.offset = this.current();
        this.buffer = next;
        this.mode = mode;
        if (this.playing) this.start();
        this.showMode();
      } catch (error) {
        if (error.name !== 'AbortError') this.status.textContent = this.dataset.error;
      } finally {
        this.busy = false;
        delete this.dataset.busy;
        this.playButton.disabled = false;
      }
    }
    showMode() {
      let label = this.mode === 'dry' ? this.dataset.dryLabel : this.dataset.wetLabel;
      let activeChain;
      let total = 0;
      let bypassed = 0;
      this.buttons.forEach(button => {
        const selected = (button.dataset.mode || button.dataset.bypass) === this.mode;
        button.setAttribute('aria-pressed', String(selected));
        if (button.hasAttribute('data-bypass')) {
          const off = selected || this.mode === 'dry';
          total++;
          if (off) bypassed++;
          const row = button.closest?.('[data-insert]');
          if (row) row.dataset.state = off ? 'off' : 'on';
          const state = (row || button).querySelector('[data-insert-state]');
          if (state) state.textContent = off ? this.dataset.off : this.dataset.on;
          if (selected) {
            activeChain = button.dataset.chain;
            label = `${button.dataset.label || button.textContent.trim()} · ${this.dataset.off}`;
          }
        }
      });
      this.tabs.forEach(tab => {
        const indicator = tab.querySelector('[data-chain-indicator]');
        if (indicator) indicator.hidden = this.mode !== 'dry' && tab.dataset.chainTab !== activeChain;
      });
      if (this.counter && this.dataset.countTemplate) {
        this.counter.textContent = this.dataset.countTemplate
          .replace('{active}', total - bypassed)
          .replace('{total}', total)
          .replace('{{ active }}', total - bypassed)
          .replace('{{ total }}', total);
      }
      this.status.textContent = label;
    }
    paint() {
      if (!this.time) return;
      const format = seconds => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
      const current = this.current() || 0;
      const duration = this.duration || 0;
      if (this.timeTotal) {
        this.time.textContent = format(current);
        this.timeTotal.textContent = format(duration);
      } else {
        this.time.textContent = `${format(current)} / ${format(duration)}`;
      }
      this.seek.value = current;
      this.seek.setAttribute('aria-valuetext', `${format(current)} / ${format(duration)}`);
      this.seek.style?.setProperty('--fill', `${duration ? (current / duration) * 100 : 0}%`);
      const label = this.playing ? this.dataset.pause : this.dataset.play;
      const text = this.playButton.querySelector('[data-play-label]');
      if (text) text.textContent = label; else this.playButton.textContent = label;
      this.playButton.dataset.playing = String(this.playing);
    }
    disconnectedCallback() {
      this.serial++;
      this.pause();
      this.controller?.abort();
      this.context?.close();
      this.context = null;
      this.duration = 0;
      this.controller = null;
    }
  });
}
